import { forwardRef, ReactNode } from 'react';
import { screen, colors } from 'components/styles/variables';

import { trackGuidesNavClick } from 'lib/Tracking';

const NAVBAR_HEIGHT = '54px';

interface Props {
  underlinedTab: string;
  scrollToRef: (refName: string) => void;
  featuredTitle: string;
  guidesTitle: string;
  reviewsTitle: string;
  slideshowsTitle: string;
  navbarOpacity: boolean;
}

const GuidesNavbar = forwardRef<HTMLDivElement, Props>(
  (
    {
      featuredTitle,
      guidesTitle,
      reviewsTitle,
      slideshowsTitle,
      scrollToRef,
      navbarOpacity,
      underlinedTab,
    }: Props,
    ref
  ) => {
    return (
      <div ref={ref} className={`navbar-root ${navbarOpacity ? 'opaque' : ''}`}>
        <div className="container" data-navbar>
          <ul>
            <GuidesNavbarLink
              isActive={underlinedTab === 'featured'}
              onClick={(): void => {
                scrollToRef('featured');
                trackGuidesNavClick('featured_nav_click');
              }}
            >
              {featuredTitle}
            </GuidesNavbarLink>
            <GuidesNavbarLink
              isActive={underlinedTab === 'guides'}
              onClick={(): void => {
                scrollToRef('guides');
                trackGuidesNavClick('guides_nav_click');
              }}
            >
              {guidesTitle}
            </GuidesNavbarLink>
            <GuidesNavbarLink
              isActive={underlinedTab === 'reviews'}
              onClick={(): void => {
                scrollToRef('reviews');
                trackGuidesNavClick('reviews_nav_click');
              }}
            >
              {reviewsTitle}
            </GuidesNavbarLink>
            <GuidesNavbarLink
              isActive={underlinedTab === 'slideshows'}
              onClick={(): void => {
                scrollToRef('slideshows');
                trackGuidesNavClick('photo_galleries_nav_click');
              }}
            >
              {slideshowsTitle}
            </GuidesNavbarLink>
          </ul>
        </div>
        <style jsx>
          {`
            .navbar-root {
              background-color: ${colors.BLACK};
              position: sticky;
              top: 0px;
              z-index: 99;
            }

            [data-navbar] {
              padding-top: 12px;
            }

            ul {
              list-style: none;
              padding: 0;
              margin: 0;
              display: flex;
            }

            .opaque {
              background-color: ${colors.BLACK};
            }

            @media screen and (min-width: ${screen.smMin}) and (max-width: ${screen.smMax}) {
              .navbar-root {
                background-color: transparent;
              }

              [data-navbar] {
                margin-top: -${NAVBAR_HEIGHT};
              }

              .opaque {
                background-color: ${colors.BLACK};
              }
            }

            @media screen and (min-width: ${screen.mdMin}) {
              .navbar-root {
                background-color: transparent;
                position: relative;
              }

              [data-navbar] {
                margin-top: -${NAVBAR_HEIGHT};
              }
            }
          `}
        </style>
      </div>
    );
  }
);

const GuidesNavbarLink = ({
  isActive,
  onClick,
  children,
}: {
  isActive: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element => (
  <li className={isActive ? 'active' : ''}>
    <button
      onClick={(): void => {
        onClick();
      }}
      onKeyPress={(): void => {
        onClick();
      }}
    >
      {children}
    </button>
    <style jsx>
      {`
        li {
          margin-right: 20px;
          font-size: 16px;
          color: white;
          font-weight: 600;
          padding-bottom: 12px;
          cursor: pointer;
        }

        li:last-child {
          margin-right: 0;
        }

        li > button {
          background-color: transparent;
          border: none;
          padding: 0;
        }

        .active {
          border-bottom: 5px solid ${colors.ALR1};
        }

        @media screen and (min-width: ${screen.smMin}) {
          li {
            font-size: 18px;
            margin-right: 40px;
          }
        }

        @media screen and (min-width: ${screen.mdMin}) {
          li:hover {
            color: ${colors.ALR1};
          }
        }
      `}
    </style>
  </li>
);

export default GuidesNavbar;
